/**
 * Top-level table for the "Properties" view mode. Renders the property list (the same data
 * shown by the card view) as a flat table with Address, Type, and Units columns plus a
 * per-row overflow menu. Rows navigate to the property when clicked.
 */
import { Fragment, useRef, useState } from 'react';
import Link from 'next/link';

import { Anchor, Box, Paragraph, Table, VisuallyHidden } from '@zillow/constellation-10';

import { getPhotoThumbUrl } from '@zg-rentals/zillow-rental-manager/utils';

import { usePortfolioFilters } from '../../../contexts/PortfolioFiltersContext';
import { useTableObserver } from '../../../hooks/queries/properties/useTableObserver';
import { useScreenSize } from '../../../hooks/useScreenSize';
import {
  getAddressLines,
  getPropertyRoute,
  getPropertyTypeLabel,
  getUnitSummary,
  isMultiplexListing,
} from '../../../utils/flexiblePropertyCardUtils';
import { cachePropertyAddress } from '../../../utils/navigationCacheUtils';

import EditMultiplexPropertyModal from '../../../components/modals/EditMultiplexPropertyModal';
import FlexibleArchivePropertyModal, {
  ARCHIVE_TARGET_NOUN,
} from '../../../components/modals/FlexibleArchivePropertyModal';
import PropertyDetailsModal from '../../../components/modals/PropertyDetailsModal';
import { FixedScrollbar } from '../../../components/properties/FixedScrollbar';
import { PropertyPhoto } from '../../../components/PropertyPhoto';
import { TruncatedTextWithTooltip } from '../../../components/TruncatedTextWithTooltip';

import {
  hiddenMobileCellStyles,
  interactiveRowStyles,
  lastRowStyles,
  overflowMenuCellStyle,
  propertyRowLinkStyles,
  truncatedTextStyles,
  unitCellContentStyles,
  unitInfoStyles,
} from '../../../styles/propertyCardTable';
import { propertyTableCellContentWidths, propertyThumbnailStyles } from '../styles';

import PropertyOverflowMenu from './PropertyOverflowMenu';
import SkeletonPropertyRows from './SkeletonPropertyRows';

import type { ValueOf } from 'type-fest';
import type {
  AccountViewListingGroupDto,
  AccountViewListingGroupType,
  RentalManagerAddressDto,
} from '@zg-rentals/ts-rental-manager-api-client';

// Secondary columns are hidden on small screens, keeping only Address and the always-visible
// overflow menu (which has no visible header label).
const PROPERTY_TABLE_COLUMNS: Array<{
  label: string;
  hideOnMobile?: boolean;
  hideLabel?: boolean;
  width?: ValueOf<typeof propertyTableCellContentWidths>;
}> = [
  { label: 'Address', width: propertyTableCellContentWidths.ADDRESS },
  { label: 'Type', hideOnMobile: true, width: propertyTableCellContentWidths.TYPE },
  { label: 'Units', hideOnMobile: true, width: propertyTableCellContentWidths.UNITS },
  { label: 'Overflow menu', hideLabel: true, width: propertyTableCellContentWidths.OVERFLOW },
];

type ArchiveTarget = {
  listingAlias: string;
  groupId: string;
  address: RentalManagerAddressDto;
  listingType: AccountViewListingGroupType;
  isArchived: boolean;
  unitCount?: number;
};

type EditTarget = {
  groupId: string;
  fullAddress: string;
  address: RentalManagerAddressDto;
  unitCount?: number;
};

type Props = {
  listingGroups: Array<AccountViewListingGroupDto>;
  isLoading?: boolean;
  hasNextPage?: boolean;
};

const PropertiesTable = ({ listingGroups, isLoading = false, hasNextPage = false }: Props) => {
  const { isScreenMobile } = useScreenSize();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { showFixedScrollbar, isTableBottomVisible } = useTableObserver({ scrollContainerRef, isScreenMobile });

  const { includeArchived: isArchiveView } = usePortfolioFilters();

  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);
  const [showMultiplexEditModal, setShowMultiplexEditModal] = useState<EditTarget | null>(null);
  const [showUnitsEditModal, setShowUnitsEditModal] = useState(false);
  const [editListingAlias, setEditListingAlias] = useState('');

  const showSkeletons = isLoading && listingGroups.length === 0;

  const renderListingRow = (listing: AccountViewListingGroupDto, index: number) => {
    const isLastRow = index === listingGroups.length - 1 && !hasNextPage;

    const { streetLine, cityStateZip } = getAddressLines(listing);
    const route = getPropertyRoute(listing);
    // Only the dashboard reads this cache, and a multiplex alias points at just one of its units.
    const opensDashboard = !isMultiplexListing(listing) && !listing.isMultifamily && Boolean(listing.listingAlias);
    const addressLabel = streetLine || 'Property';

    const row = (
      <Table.Row
        interactive={Boolean(route)}
        css={{
          ...(route ? interactiveRowStyles : { cursor: 'default' }),
          ...(isLastRow ? lastRowStyles : {}),
        }}
      >
        {/* address */}
        <Table.Cell>
          <Box css={unitCellContentStyles}>
            <Box css={propertyThumbnailStyles}>
              <PropertyPhoto src={getPhotoThumbUrl(listing.photoKey)} alt={addressLabel} />
            </Box>
            <Box css={unitInfoStyles}>
              <TruncatedTextWithTooltip text={streetLine || '—'} textStyle="body-sm-bold" css={truncatedTextStyles} />
              <TruncatedTextWithTooltip
                text={cityStateZip}
                textStyle="body-xs"
                color="text.subtle"
                css={truncatedTextStyles}
              />
            </Box>
          </Box>
        </Table.Cell>

        {/* type */}
        <Table.Cell css={hiddenMobileCellStyles}>
          <Paragraph textStyle="body-sm">{getPropertyTypeLabel(listing) ?? '—'}</Paragraph>
        </Table.Cell>

        {/* units */}
        <Table.Cell css={hiddenMobileCellStyles}>
          <Paragraph textStyle="body-sm">{getUnitSummary(listing)}</Paragraph>
        </Table.Cell>

        {/* overflow menu (don't let clicks here follow the row's link or bubble to other handlers) */}
        <Table.Cell
          css={overflowMenuCellStyle}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <PropertyOverflowMenu
            groupId={listing.groupId}
            listingAlias={listing.listingAlias}
            addressLabel={streetLine}
            address={listing.address}
            onArchiveClick={(listingAlias, address) =>
              setArchiveTarget({
                listingAlias,
                groupId: listing.groupId,
                address,
                listingType: listing.type,
                isArchived: isArchiveView,
                unitCount: listing.listingCount?.total,
              })
            }
            onEditClick={
              isMultiplexListing(listing)
                ? () =>
                    setShowMultiplexEditModal({
                      groupId: listing.groupId,
                      fullAddress: `${streetLine} ${cityStateZip}`.trim(),
                      address: listing.address,
                      unitCount: listing.listingCount?.total,
                    })
                : () => {
                    setEditListingAlias(listing.listingAlias);
                    setShowUnitsEditModal(true);
                  }
            }
          />
        </Table.Cell>
      </Table.Row>
    );

    // wrap the row in a link if the listing has a route
    return route ? (
      <Anchor key={listing.groupId} asChild aria-label={addressLabel} css={propertyRowLinkStyles}>
        <Link
          href={route}
          onClick={opensDashboard ? () => cachePropertyAddress(listing.listingAlias, listing.address) : undefined}
        >
          {row}
        </Link>
      </Anchor>
    ) : (
      <Fragment key={listing.groupId}>{row}</Fragment>
    );
  };

  return (
    <Box css={{ overflow: { base: 'auto', lg: 'hidden' } }}>
      <Box ref={scrollContainerRef} css={{ overflowX: { base: 'hidden', lg: 'auto' }, maxWidth: '100%' }}>
        <Table.Root
          data-testid="PropertiesTable"
          aria-label="Properties"
          appearance="horizontal"
          css={{ width: '100%', tableLayout: 'fixed' }}
        >
          {/* header */}
          <Table.Header>
            <Table.Row>
              {PROPERTY_TABLE_COLUMNS.map((column) => (
                <Table.HeaderCell
                  key={column.label}
                  css={{
                    ...(column.hideOnMobile ? hiddenMobileCellStyles : {}),
                    ...column.width,
                  }}
                >
                  {column.hideLabel ? (
                    <VisuallyHidden>{column.label}</VisuallyHidden>
                  ) : (
                    <Paragraph textStyle="body-sm-bold">{column.label}</Paragraph>
                  )}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {/* Skeletons */}
            {showSkeletons && <SkeletonPropertyRows />}

            {/* Listings */}
            {!showSkeletons && listingGroups.map(renderListingRow)}
          </Table.Body>
        </Table.Root>
      </Box>

      <FixedScrollbar
        scrollContainerRef={scrollContainerRef}
        isVisible={showFixedScrollbar}
        isTableBottomVisible={isTableBottomVisible}
      />

      {archiveTarget && (
        <FlexibleArchivePropertyModal
          onClose={() => setArchiveTarget(null)}
          isArchived={archiveTarget.isArchived}
          listingAlias={archiveTarget.listingAlias}
          groupId={archiveTarget.groupId}
          address={archiveTarget.address}
          targetNoun={ARCHIVE_TARGET_NOUN.PROPERTY}
          listingType={archiveTarget.listingType}
          unitCount={archiveTarget.unitCount}
        />
      )}

      {showMultiplexEditModal && (
        <EditMultiplexPropertyModal
          isOpen={true}
          groupId={showMultiplexEditModal.groupId}
          fullAddress={showMultiplexEditModal.fullAddress}
          address={showMultiplexEditModal.address}
          unitCount={showMultiplexEditModal.unitCount}
          onClose={() => setShowMultiplexEditModal(null)}
          onSuccess={() => setShowMultiplexEditModal(null)}
        />
      )}

      {showUnitsEditModal && (
        <PropertyDetailsModal
          isOpen={true}
          listingAlias={editListingAlias}
          submitLabel="Save changes"
          onClose={() => setShowUnitsEditModal(false)}
          onConfirm={() => setShowUnitsEditModal(false)}
        />
      )}
    </Box>
  );
};

export default PropertiesTable;
